import { useEffect, useState } from "react";
import { Mail, MessageCircle, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { MaskedLine, Reveal } from "../components/Motion";

const CHANNELS = [
  { icon: Mail, label: "Email", value: "hello@saltstonehikes.com", href: "mailto:hello@saltstonehikes.com", tid: "contact-email" },
  { icon: Phone, label: "Phone", value: "+30 6976 231 058", href: "tel:+306976231058", tid: "contact-phone" },
  { icon: MessageCircle, label: "WhatsApp", value: "+30 698 326 7064", href: "https://wa.me/306983267064", tid: "contact-whatsapp" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Contact — Salt & Stone, Hydra";
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  const inputCls =
    "w-full rounded-xl border border-ink/20 bg-white px-4 py-3.5 text-lg text-ink outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-ink/35 focus:border-terra focus:ring-2 focus:ring-terra/20";

  return (
    <main data-testid="contact-page" className="pt-28 sm:pt-36">
      <header className="mx-auto max-w-7xl px-5 sm:px-8">
        <MaskedLine>
          <span className="overline-label text-terra">Contact</span>
        </MaskedLine>
        <MaskedLine delay={0.12}>
          <h1 className="display-serif mt-4 max-w-3xl text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" data-testid="contact-title">
            Talk to Vasilis
          </h1>
        </MaskedLine>
        <Reveal delay={0.25}>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink/70">
            Questions about a trail, the season, or a last-minute booking? Write, call or message —
            every enquiry lands directly with your guide.
          </p>
        </Reveal>
      </header>

      <section className="mx-auto max-w-7xl gap-12 px-5 py-16 sm:px-8 sm:py-24 lg:grid lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="flex flex-col gap-4" data-testid="contact-channels">
            {CHANNELS.map((c, i) => (
              <Reveal key={c.tid} delay={i * 0.08}>
                <a
                  href={c.href}
                  data-testid={c.tid}
                  className="group flex items-center gap-5 rounded-2xl border border-ink/15 bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:border-terra/40 hover:shadow-[0_18px_40px_-24px_rgba(28,25,23,0.35)]"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-terra/10 text-terra transition-colors duration-300 group-hover:bg-terra group-hover:text-paper">
                    <c.icon size={20} />
                  </span>
                  <span>
                    <span className="block text-sm uppercase tracking-[0.2em] text-ink/45">{c.label}</span>
                    <span className="mt-1 block text-lg font-medium text-ink">{c.value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-8 rounded-2xl bg-olive-pale/60 p-6 text-base leading-relaxed text-ink/65">
              Vasilis is often on the mountain between 8:00 and 15:00 — if the phone goes quiet,
              WhatsApp or email always reaches him by evening.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 lg:col-span-7 lg:mt-0">
          <Reveal delay={0.15}>
            {sent ? (
              <div data-testid="contact-success" className="rounded-3xl border border-olive/30 bg-olive-pale p-9">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-olive text-paper">
                  <Send size={20} />
                </span>
                <h2 className="mt-6 font-serif text-3xl tracking-tight">Message sent.</h2>
                <p className="mt-4 text-base leading-relaxed text-ink/70">
                  Thank you — your enquiry is on its way to Vasilis. Expect a personal reply at your email address,
                  usually within a day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-ink/15 bg-white p-7 sm:p-9">
                <h2 className="font-serif text-3xl tracking-tight">Send an enquiry</h2>
                <label className="mt-7 block">
                  <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Full name</span>
                  <input type="text" required minLength={2} value={form.name} onChange={set("name")}
                    placeholder="Alexandra Meyer" data-testid="contact-name-input" className={inputCls} />
                </label>
                <label className="mt-5 block">
                  <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Email</span>
                  <input type="email" required value={form.email} onChange={set("email")}
                    placeholder="you@example.com" data-testid="contact-email-input" className={inputCls} />
                </label>
                <label className="mt-5 block">
                  <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Message</span>
                  <textarea rows={5} required minLength={5} value={form.message} onChange={set("message")}
                    placeholder="Which hike are you dreaming of? Dates, fitness, questions…"
                    data-testid="contact-message-input" className={inputCls} />
                </label>
                <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                  className="mt-7 w-full rounded-full bg-terra py-4 text-base uppercase tracking-[0.18em] text-paper transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-terra-dark disabled:opacity-50">
                  {sending ? "Sending…" : "Send message"}
                </button>
                <p className="mt-4 text-center text-sm text-ink/45">
                  Goes straight to hello@saltstonehikes.com — no newsletters, ever.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
